import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSyncAlt, faTimes } from "@fortawesome/free-solid-svg-icons"
import useUpdateAvailable from "../../../hooks/useUpdateAvailable"
import "./index.scss"

// If the waiting worker never activates -- it was already discarded, the
// browser is in a state we did not anticipate -- reload anyway rather than
// leaving someone looking at a button that did nothing.
export const RELOAD_FALLBACK_MS = 3000

/**
 * "New version ready" -- shown once a newly deployed service worker has
 * finished installing, offering the reload that swaps in the new build.
 *
 * This banner is the only thing that promotes a waiting worker FOR A CLIENT THAT
 * CAN RENDER IT. sw-custom.js does not call skipWaiting() at the top level, so
 * until the button is tapped the old worker keeps serving the chunks the running
 * page expects and nothing breaks underneath the user. utils/staleBuild.js stays
 * as the net for the cases this does not cover.
 *
 * The qualification is new as of 27 September 2026 and the sentence used to have
 * none. A build that does not contain this banner cannot promote a worker at all,
 * so it sat on the same build forever; sw-custom.js now promotes itself for such
 * a client. The existence of THIS FILE is what index.js is asserting when it
 * answers that capability query with canOfferUpdate: true. If this component is
 * ever removed, that answer becomes untrue and has to change in the same commit,
 * or every client quietly opts itself out of both paths and nothing updates
 * anybody. See utils/serviceWorkerCapability.js.
 *
 * Which is why it prompts rather than reloading by itself: a child may be
 * halfway through a question, and taking their work away to fix a problem they
 * cannot see is the wrong trade.
 *
 * Dismissal is per-announcement, not permanent. A page-view in this app can
 * last months (see utils/serviceWorkerUpdates.js), so a remembered "no thanks"
 * would recreate the stale-client problem this exists to fix -- the next deploy
 * announces a new sequence and the banner returns.
 *
 * `win` is injected so the reload can be asserted on; jsdom's window.location is
 * not spy-able. utils/staleBuild.js takes the same seam for the same reason.
 */
const UpdateAvailableBanner = ({ win = window }) => {
	const update = useUpdateAvailable()
	const [dismissedSequence, setDismissedSequence] = useState(null)
	const [pending, setPending] = useState(false)

	if (!update || dismissedSequence === update.sequence) return null

	const reload = () => {
		if (pending) return

		const { waiting } = update.registration
		const container = win.navigator && win.navigator.serviceWorker

		// Nothing to promote -- either the worker activated on its own or this
		// browser has no controller to swap. A plain reload is all there is.
		if (!waiting || !container) {
			win.location.reload()

			return
		}

		setPending(true)

		// postMessage is asynchronous, so reloading on the next line would race
		// the activation and could land the user right back on the old build.
		// Wait for the controller to actually change.
		let reloaded = false
		const go = () => {
			if (reloaded) return
			reloaded = true
			win.location.reload()
		}

		container.addEventListener("controllerchange", go)
		win.setTimeout(go, RELOAD_FALLBACK_MS)

		waiting.postMessage({ type: "SKIP_WAITING" })
	}

	return (
		<div className="UpdateAvailableBanner" role="status">
			<div className="banner-content">
				<FontAwesomeIcon icon={faSyncAlt} className={`update-icon${pending ? " spinning" : ""}`} />
				<div className="text">
					<strong>New version ready</strong>
					<span>Reload to get the latest lessons and fixes</span>
				</div>
			</div>
			<div className="banner-actions">
				<button type="button" className="reload-btn" onClick={reload} disabled={pending}>
					{pending ? "Updating…" : "Reload"}
				</button>
				<button
					type="button"
					className="dismiss-btn"
					onClick={() => setDismissedSequence(update.sequence)}
					aria-label="Dismiss update notice"
					disabled={pending}
				>
					<FontAwesomeIcon icon={faTimes} />
				</button>
			</div>
		</div>
	)
}

export default UpdateAvailableBanner
